/**
 * Which window the dashboard is being read over.
 *
 * Every figure on this page used to print six periods at once -- today, this
 * week, this month, and the rolling 24 hours, 7 days and 30 days -- so a
 * section of four widgets was twenty-four numbers, and finding the one you
 * came for meant reading all of them. The periods were never a question the
 * reader had; they were every answer at once.
 *
 * So the section asks the question, and the figures under it answer only that.
 *
 * A CONTEXT, NOT A PROP. These widgets are used outside the dashboard too, and
 * threading a period through every one would mean editing callers that have no
 * opinion about periods. Without a provider `usePeriod` returns null and each
 * widget shows everything exactly as it did, which is the behaviour anywhere
 * else in the product.
 *
 * CALENDAR, NOT ROLLING. "This week" counts from Monday; "last 7 days" counts
 * from now, and at 9am on a Monday they are almost entirely different numbers.
 * Both used to be shown side by side, which doubled the figures to halve the
 * ambiguity -- the wrong trade for a screen somebody glances at. The tab says
 * Today, Week or Month, and those are calendar windows in the zone named at
 * the top of the page. The rolling windows are still fetched and still exist
 * on the endpoints; nothing on this page shows them.
 */
import { createContext, useContext } from 'react'

export type Period = 'live' | 'today' | 'week' | 'month' | 'all' | 'custom'

/** A custom window: whole days in the dashboard's zone, both ends included. */
export type Range = { from: string; to: string }

export const PERIOD_OPTIONS: [key: Period, label: string][] = [
    ['today', 'Today'],
    ['week', 'Week'],
    ['month', 'Month'],
    ['all', 'All time'],
    ['custom', 'Custom'],
]

type Window = { period: Period; range: Range | null }

const PeriodContext = createContext<Window | null>(null)

export const PeriodProvider = PeriodContext.Provider

/** The chosen period, or null when nothing on this screen chose one. */
export const usePeriod = () => useContext(PeriodContext)?.period ?? null

/** The custom range, when the chosen period is Custom. */
export const useRange = () => {
    const window = useContext(PeriodContext)
    return window?.period === 'custom' ? window.range : null
}

/** The key a figure's own answer is stored under for the chosen period.
 *  All time is the endpoints' lifetime figure, which they call "total". */
export const periodKey = (period: Period) => (period === 'all' ? 'total' : period)

/**
 * What a figure's endpoints need to be asked for the chosen window.
 *
 * `figure` goes on the call for the number: the range, when there is one, so
 * the endpoint can answer a "custom" period. `history` goes on the call for
 * the chart: by month for All time, by day across a range, else the last 31
 * days, which covers the longest month. `key` changes whenever either does,
 * for a widget to refetch on.
 */
export const useWindowParams = () => {
    const period = usePeriod()
    const range = useRange()
    const figure = range ? { from: range.from, to: range.to } : {}
    const history = period === 'all' ? { days: 0, monthly: true } : range ? { days: 0, ...figure } : { days: 31 }
    const key = `${period === 'all' ? 'all' : ''}${range ? `${range.from}_${range.to}` : ''}`
    return { figure, history, key, custom: Boolean(range) }
}
